import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Search, LogOut, Download, Trash2, Mail, Phone, MapPin, Package, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, getToken } from "@/lib/adminApi";
import { BRAND } from "@/data/content";

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const authed = !!getToken();
  const [loading, setLoading] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listEnquiries();
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminApi.logout();
        nav("/admin/login", { replace: true });
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return enquiries.filter((e) => {
      if (source !== "all" && (e.source || "contact") !== source) return false;
      if (!q) return true;
      return [e.full_name, e.email, e.mobile, e.city, e.product_requirement, e.message]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [enquiries, query, source]);

  const stats = useMemo(() => ({
    total: enquiries.length,
    quotes: enquiries.filter((e) => e.source === "quote").length,
    today: enquiries.filter((e) => (e.created_at || "").slice(0, 10) === new Date().toISOString().slice(0, 10)).length,
  }), [enquiries]);

  if (!authed) return <Navigate to="/admin/login" replace />;

  const logout = () => { adminApi.logout(); nav("/admin/login", { replace: true }); };

  const exportCsv = () => {
    const headers = ["Date","Name","Mobile","Email","City","Product","Source","Message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((e) => [fmt(e.created_at), e.full_name, e.mobile, e.email, e.city, e.product_requirement, e.source, e.message].map(esc).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vernika-enquiries-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    try {
      await adminApi.deleteEnquiry(id);
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-[#0a0a0a]/90 backdrop-blur border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Vernika" className="h-9 w-9 rounded-full ring-1 ring-[#e7c15a]/40" />
            <div>
              <div className="font-serif-lux text-sm text-gold-gradient tracking-[0.15em] leading-none">VERNIKA</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#b8b8b8] mt-0.5">Admin Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={load} className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#b8b8b8] hover:text-[#e7c15a]" data-testid="admin-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#e8e8e8] hover:border-[#e7c15a] hover:text-[#e7c15a] transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-8 md:py-12">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-px bg-white/5 border border-white/5 mb-8">
          {[["Total Enquiries", stats.total, "total"],["Quote Requests", stats.quotes, "quotes"],["Today", stats.today, "today"]].map(([label, val, k]) => (
            <div key={k} className="bg-[#0a0a0a] p-5 md:p-6" data-testid={`stat-${k}`}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#b8b8b8]">{label}</div>
              <div className="mt-2 font-serif-lux text-3xl md:text-4xl text-gold-gradient">{val}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#666]" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, mobile, city, product…"
              className="w-full bg-[#0d0d0d] border border-white/10 pl-10 pr-4 py-3 text-sm text-[#fafafa] outline-none focus:border-[#e7c15a]"
            />
          </div>
          <div className="flex gap-1 bg-[#0d0d0d] border border-white/10 p-1">
            {[["all","All"],["contact","Contact"],["quote","Quote"]].map(([k, l]) => (
              <button key={k} onClick={() => setSource(k)} data-testid={`filter-${k}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${source === k ? "bg-[#e7c15a] text-[#050505]" : "text-[#b8b8b8] hover:text-[#e7c15a]"}`}>{l}</button>
            ))}
          </div>
          <button onClick={exportCsv} data-testid="admin-export" className="flex items-center gap-2 border border-white/15 px-4 py-3 text-xs uppercase tracking-[0.2em] text-[#e8e8e8] hover:border-[#e7c15a] hover:text-[#e7c15a] transition-colors">
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Table + Detail */}
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="border border-white/10 bg-[#0a0a0a] overflow-hidden">
            {loading ? (
              <div className="p-16 flex flex-col items-center gap-3 text-[#b8b8b8]">
                <Loader2 className="animate-spin" size={22} />
                <span className="text-sm">Loading enquiries…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center text-[#b8b8b8]">
                <Mail size={28} className="mx-auto mb-4 text-[#e7c15a]/60" />
                <div className="font-serif-lux text-2xl text-[#fafafa]">No enquiries yet.</div>
                <div className="text-sm mt-2">New customer submissions will appear here.</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.2em] text-[#b8b8b8] border-b border-white/10">
                      <th className="text-left px-5 py-3">Date</th>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Mobile</th>
                      <th className="text-left px-5 py-3 hidden md:table-cell">Product</th>
                      <th className="text-left px-5 py-3 hidden md:table-cell">Source</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e) => (
                      <tr key={e.id} onClick={() => setSelected(e)} data-testid={`row-${e.id}`}
                        className={`border-b border-white/5 cursor-pointer transition-colors ${selected?.id === e.id ? "bg-[#141005]" : "hover:bg-[#0d0d0d]"}`}>
                        <td className="px-5 py-4 text-[#b8b8b8] whitespace-nowrap">{fmt(e.created_at)}</td>
                        <td className="px-5 py-4 text-[#fafafa] font-medium">{e.full_name}</td>
                        <td className="px-5 py-4 text-[#e8e8e8]">{e.mobile}</td>
                        <td className="px-5 py-4 text-[#b8b8b8] hidden md:table-cell">{e.product_requirement || "—"}</td>
                        <td className="px-5 py-4 hidden md:table-cell">
                          <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${e.source === "quote" ? "border-[#e7c15a] text-[#e7c15a]" : "border-white/15 text-[#b8b8b8]"}`}>{e.source || "contact"}</span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button onClick={(ev) => { ev.stopPropagation(); removeEnquiry(e.id); }} className="text-[#666] hover:text-red-400" data-testid={`delete-${e.id}`} title="Delete">
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Detail panel */}
          <aside className="border border-white/10 bg-[#0a0a0a] p-6 md:p-7 h-fit lg:sticky lg:top-24">
            {selected ? (
              <div data-testid="enquiry-detail">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#e7c15a]">{selected.source || "contact"}</div>
                <h2 className="mt-2 font-serif-lux text-2xl text-[#fafafa]">{selected.full_name}</h2>
                <div className="mt-1 text-xs text-[#b8b8b8]">{fmt(selected.created_at)}</div>

                <div className="mt-6 space-y-4 text-sm">
                  <div className="flex gap-3">
                    <Phone size={15} className="text-[#e7c15a] shrink-0 mt-0.5" />
                    <a href={`tel:${selected.mobile}`} className="text-[#e8e8e8] hover:text-[#e7c15a]">{selected.mobile}</a>
                  </div>
                  <div className="flex gap-3">
                    <Mail size={15} className="text-[#e7c15a] shrink-0 mt-0.5" />
                    <a href={`mailto:${selected.email}`} className="text-[#e8e8e8] hover:text-[#e7c15a] break-all">{selected.email}</a>
                  </div>
                  {selected.city && (
                    <div className="flex gap-3">
                      <MapPin size={15} className="text-[#e7c15a] shrink-0 mt-0.5" />
                      <span className="text-[#e8e8e8]">{selected.city}</span>
                    </div>
                  )}
                  {selected.product_requirement && (
                    <div className="flex gap-3">
                      <Package size={15} className="text-[#e7c15a] shrink-0 mt-0.5" />
                      <span className="text-[#e8e8e8]">{selected.product_requirement}</span>
                    </div>
                  )}
                </div>

                {selected.message && (
                  <div className="mt-6 border-t border-white/10 pt-5">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#b8b8b8] mb-2">Message</div>
                    <p className="text-sm text-[#e8e8e8] whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                  </div>
                )}

                <div className="mt-7 grid grid-cols-2 gap-3">
                  <a href={`tel:${selected.mobile}`} className="text-center border border-[#e7c15a] py-3 text-xs uppercase tracking-[0.2em] text-[#e7c15a] hover:bg-[#e7c15a] hover:text-[#050505] transition-colors">Call</a>
                  <a href={`https://wa.me/${selected.mobile.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-center bg-[#25D366] text-[#050505] py-3 text-xs uppercase tracking-[0.2em] font-medium">WhatsApp</a>
                </div>
              </div>
            ) : (
              <div className="text-center text-[#b8b8b8] py-10">
                <div className="text-sm">Select an enquiry to see details.</div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
